import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { faEdit, faTrash, faSearch, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import Pagination from '../../components/Pagination';
import ConfirmationModal from '../../components/ConfirmationModal';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import leadApi from '../../api/leadApi';
import { useToast } from '../../contexts/ToastContext';

const ContactLeads = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsPerPage] = useState(10);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [leadToDeleteId, setLeadToDeleteId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [error, setError] = useState(null);

  const [lastPage, setLastPage] = useState(1);

  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const fetchLeads = useCallback(async (page, perPage, search) => {
    setLoading(true);
    setError(null);
    try {
      const response = await leadApi.getLeads({ page, perPage, search, includeTrashed: false });
      setLeads(response.data || []);
      setLastPage(response.pagination ? response.pagination.last_page : 1);
    } catch (err) {
      console.error('Failed to fetch leads:', err);
      setError('Failed to load leads. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads(currentPage, itemsPerPage, debouncedSearchTerm);
  }, [fetchLeads, currentPage, itemsPerPage, debouncedSearchTerm]);

  const handleEditLead = (id) => {
    navigate(`/contact-leads/${id}/edit`);
  };

  const handleDeleteClick = (id) => {
    setLeadToDeleteId(id);
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    setIsModalOpen(false);
    if (leadToDeleteId) {
      setLoading(true);
      setError(null);
      let errorMessage = 'Failed to move lead to trash. Please try again.'; // Declare errorMessage here
      try {
        await leadApi.trashLead(leadToDeleteId);
        addToast('Lead moved to trash successfully!', 'success');
        await fetchLeads(currentPage, itemsPerPage, debouncedSearchTerm);
      } catch (err) {
        console.error('Error trashing lead:', err);
        errorMessage = err.response?.data?.message || errorMessage; // Assign to the declared variable
        addToast(errorMessage, 'error');
      } finally {
        setLoading(false);
        setLeadToDeleteId(null);
        setError(errorMessage); // Use the defined errorMessage
      }
    }
  };

  const handleCancelDelete = () => {
    setIsModalOpen(false);
    setLeadToDeleteId(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white flex flex-col min-h-[calc(100vh-100px)]">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6"> {/* Modified for responsiveness */}
        <h1 className="text-3xl font-bold text-text-white mb-4 sm:mb-0">Contact Leads</h1> {/* Added mb-4 for mobile spacing */}
        <div className="flex space-x-4">
          <Button
            onClick={() => navigate('/contact-leads/trash')}
            className="bg-gray-600 hover:bg-gray-700 text-white"
            icon={faTrashAlt}
            tooltipId="trash-leads-tooltip"
            tooltipContent="View Trashed Leads"
          >
            Trash
          </Button>
        </div>
      </div>

      <div className="mb-4">
        <TextInput
          label="Search Leads"
          id="search-leads"
          name="search-leads"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by name, email, or subject..."
          icon={faSearch}
        />
      </div>

      {loading ? (
        <SkeletonLoader type="table" count={itemsPerPage} />
      ) : error ? (
        <div className="text-red-500 text-center py-4">{error}</div>
      ) : (
        <>
          <div className="overflow-x-auto flex-grow">
            <table className="min-w-full bg-gray-800 border border-gray-700">
              <thead>
                <tr>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">ID</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Name</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Email</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Subject</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Status</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Updated At</th> {/* New column */}
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.length > 0 ? (
                  leads.map((lead) => (
                    <tr key={lead.id} className="hover:bg-gray-700">
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{lead.id}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{lead.name}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{lead.email}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{lead.subject}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                        {lead.lead_status ? (
                          <span
                            className="px-2 py-1 rounded-full text-xs font-semibold"
                            style={{ backgroundColor: lead.lead_status.color || '#6B7280', color: '#FFFFFF' }}
                          >
                            {lead.lead_status.name}
                          </span>
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                        {new Date(lead.updated_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td> {/* Display updated_at */}
                      <td className="py-3 px-4 border-b border-gray-700 text-sm flex items-center flex-nowrap">
                        <Button
                          onClick={() => handleEditLead(lead.id)}
                          className="text-yellow-500 hover:text-yellow-600 mr-3"
                          icon={faEdit}
                          tooltipId={`edit-lead-tooltip-${lead.id}`}
                          tooltipContent="Edit"
                        />
                        <Button
                          onClick={() => handleDeleteClick(lead.id)}
                          className="text-red-600 hover:text-red-700"
                          icon={faTrash}
                          tooltipId={`delete-lead-tooltip-${lead.id}`}
                          tooltipContent="Delete"
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="py-3 px-4 text-center text-sm text-text-gray">No leads found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={lastPage}
          />
        </>
      )}

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Confirm Delete"
        message="Are you sure you want to move this lead to trash?"
      />
    </div>
  );
};

export default ContactLeads;